import { useMemo } from 'react'

import { Filter, SubCollection } from '../menu/filterSection.interface'
import { getTitleFromPath } from '../menu/filterUtils'

/**
 * Calculate total selected filters including URL-based selections
 * Uses a consistent calculation method between server and client to prevent hydration mismatches
 */
export function useEnhancedFilterCount(
  selectedFiltersState: Record<string, string[]>,
  showGroupedPage: boolean,
  routerAsPath: string,
  serverFilters: Filter[],
  subCollectionCards?: SubCollection[],
) {
  // Calculate total count in a single useMemo to ensure consistency
  return useMemo(() => {
    // Calculate base count from selectedFiltersState
    const baseCount = Object.values(selectedFiltersState).reduce((total: number, filterValues) => {
      if (!filterValues || !Array.isArray(filterValues)) return total
      return total + filterValues.length
    }, 0)

    // For non-grouped pages or missing data, return only base count
    if (!showGroupedPage || !routerAsPath || !serverFilters?.length) {
      return baseCount
    }

    // Get title from current path using subcollection cards
    const typedSubCollectionCards = subCollectionCards?.map(card => ({
      id: String(card.id),
      title: card.title,
      slug: card.slug,
    }))
    const currentTitle = getTitleFromPath(routerAsPath, typedSubCollectionCards)
    if (!currentTitle) {
      return baseCount
    }

    // Check if the title is already in selectedFiltersState
    const isAlreadySelected = Object.values(selectedFiltersState).some(values => values.includes(currentTitle))

    return baseCount + (isAlreadySelected ? 0 : 1)
  }, [selectedFiltersState, showGroupedPage, routerAsPath, serverFilters, subCollectionCards])
}
